import { createContext, useContext, useMemo } from "react";
import PropTypes from "prop-types";

/* Builds a { submoduleName: Set<action> } map straight from the
   get-user API's `roles` payload, so route guards and inline gates
   stay reactive to the logged-in admin without re-reading localStorage. */
const buildPermissionMap = (roles) => {
  const permissionMap = new Map();

  if (!roles || typeof roles !== "object") {
    return permissionMap;
  }

  for (const moduleKey in roles) {
    const module = roles[moduleKey];
    if (!module?.submodules) continue;

    for (const submoduleKey in module.submodules) {
      const submodule = module.submodules[submoduleKey];
      const actions = new Set(
        (submodule?.permissions || [])
          .map((permission) => permission?.action)
          .filter(Boolean),
      );
      permissionMap.set(submodule?.name, actions);
    }
  }

  return permissionMap;
};

const PermissionContext = createContext({
  isSuperAdmin: false,
  canAccess: () => true,
  hasModuleAccess: () => true,
});

export const PermissionProvider = ({ roles, roleId, children }) => {
  const value = useMemo(() => {
    const isSuperAdmin = String(roleId) === "7";
    const permissionMap = buildPermissionMap(roles);

    const hasModuleAccess = (module) => {
      if (isSuperAdmin || !module) return true;

      const moduleNames = Array.isArray(module) ? module : [module];
      return moduleNames.some((name) => (permissionMap.get(name)?.size ?? 0) > 0);
    };

    /* module can be a single submodule name or an array of names to check
       with OR semantics (some pages accept access via either of two related
       submodules). `action` must match exactly what the API returns for
       that submodule (e.g. "View", "View Loan", "Show Kyc", "Check") — the
       route config in routes/index.jsx and the sidebar entries in
       util/General.js both use these exact same action/module strings, so
       a role only sees + can open pages it truly has that action for. If
       `action` is omitted, falls back to "has ANY permission on the
       module" (used by callers that just want a coarse access check). */
    const canAccess = (module, action) => {
      if (isSuperAdmin || !module) return true;
      if (!action) return hasModuleAccess(module);

      const moduleNames = Array.isArray(module) ? module : [module];
      return moduleNames.some((name) => permissionMap.get(name)?.has(action));
    };

    return { isSuperAdmin, canAccess, hasModuleAccess };
  }, [roles, roleId]);

  return (
    <PermissionContext.Provider value={value}>
      {children}
    </PermissionContext.Provider>
  );
};

PermissionProvider.propTypes = {
  roles: PropTypes.object,
  roleId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  children: PropTypes.node,
};

export const usePermission = () => useContext(PermissionContext);
